import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { BP_FAQ } from "@/lib/usecases";
import Footer from "@/components/berlin/Footer";
import { BrandWordmark } from "@/components/berlin/BrandWordmark";
import {
  ArrowLeft,
  ArrowUpRight,
  ArrowDown,
  MoveHorizontal,
  ShieldCheck,
  Layers,
  Leaf,
  Quote,
  Plus,
} from "lucide-react";

/*
  Landing-Unterseite · /breitscheidplatz
  Modellprojekt-Vorschlag · Beitrag zur laufenden Debatte
  Ton: respektvoll gegenüber Senatsplanung, kein Vergleich, kein Anspruch.
*/

const HERO_BG =
  "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/td8tme20_grafik.png";

const PAIRS = [
  {
    key: "breitscheidplatz-wide",
    label: "Breitscheidplatz · Weite Perspektive",
    before:
      "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/td8tme20_grafik.png",
    after:
      "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/luwd6n2a_ChatGPT%20Image%205.%20Juli%202026%2C%2016_55_29.png",
    afterFocus: "center 42%",
  },
  {
    key: "gedaechtniskirche",
    label: "Gedächtniskirche · Vorplatz",
    before:
      "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/ax9e1xy8_grafik.png",
    after:
      "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/1fm4q4y1_ChatGPT%20Image%205.%20Juli%202026%2C%2017_09_54.png",
    afterFocus: "100% center",
  },
];

function BeforeAfterSlider({ before, after, afterFocus, label }) {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const move = useCallback((clientX) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      e.preventDefault();
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      move(cx);
    };
    const onUp = () => {
      dragging.current = false;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [move]);

  return (
    <div
      ref={ref}
      className="relative w-full aspect-[16/10] border border-white/10 overflow-hidden select-none cursor-ew-resize bg-[#0F1114]"
      onMouseDown={(e) => {
        dragging.current = true;
        move(e.clientX);
      }}
      onTouchStart={(e) => {
        dragging.current = true;
        move(e.touches[0].clientX);
      }}
      data-testid={`bp-slider-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <img
        src={after}
        alt={`Visualisierung: ${label} mit Berlin-Cube-Modulen`}
        className="absolute inset-0 w-full h-full object-cover"
        style={afterFocus ? { objectPosition: afterFocus } : undefined}
      />
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <img
          src={before}
          alt={`${label} — Foto heute`}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="absolute top-4 left-4 z-10 text-[10px] tracking-[0.35em] uppercase text-white/85 bg-black/50 backdrop-blur px-3 py-1.5 border border-white/10 font-display">
        Heute · Foto
      </div>
      <div className="absolute top-4 right-4 z-10 text-[10px] tracking-[0.35em] uppercase text-[#F5D77A] bg-black/50 backdrop-blur px-3 py-1.5 border border-[#F5D77A]/30 font-display">
        Vision · Konzeptvisualisierung
      </div>
      <div
        className="absolute top-0 bottom-0 w-px bg-white/90 pointer-events-none"
        style={{ left: `${pos}%` }}
      />
      <div
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-20 w-12 h-12 rounded-full bg-white text-[#1F2328] shadow-2xl flex items-center justify-center border-4 border-[#1F2328] pointer-events-none"
        style={{ left: `${pos}%` }}
      >
        <MoveHorizontal className="h-5 w-5" strokeWidth={2} />
      </div>
    </div>
  );
}

const PROPOSAL_CARDS = [
  {
    icon: ShieldCheck,
    title: "Geprüfte Sicherheit",
    body:
      "DEKRA-Crashtest nach IWA 14-1:2013 · Testfahrzeug 7.114 kg · 63,9 km/h · DEKRA-Prüfbericht Nr. 201844624 · Europäisches Patent EP 3 486 374. Details zu Prüfaufbau und -bedingungen im Bereich Technologie.",
    linkLabel: "Zum Technologie-Kapitel",
    linkHref: "/#technology",
  },
  {
    icon: Layers,
    title: "Ohne jahrelangen Tiefbau",
    body:
      "Modulare Bauweise: einzelne Standorte in Tagen statt Jahren aufgebaut. Kein tiefes Fundament, keine dauerhafte Beeinträchtigung der Fläche.",
  },
  {
    icon: Leaf,
    title: "Mehr als Schutz",
    body:
      "Stadtgrün, Sitzinseln, Verschattung und Smart-City-Funktionen — auf denselben Standflächen. Ein Platz gewinnt Aufenthaltsqualität statt sie zu verlieren.",
  },
];

export default function Breitscheidplatz() {
  useEffect(() => {
    // SEO — client side (kein SSR, aber gut genug für Preview + Suchmaschinen-Crawl).
    // Wir merken uns, welche Meta/Link-Elemente WIR angelegt haben, um sie beim
    // Unmount wieder zu entfernen und die vorherigen Werte wiederherzustellen.
    const prevTitle = document.title;
    document.title =
      "Breitscheidplatz: Schutz ohne Barrikaden — ein Vorschlag von Berlin-Cube";

    const created = []; // Elemente, die wir neu ins <head> gehängt haben
    const restored = []; // { el, attr, prev }

    const setMeta = (name, content, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
        created.push(el);
      } else {
        restored.push({ el, attr: "content", prev: el.getAttribute("content") });
      }
      el.setAttribute("content", content);
    };

    const desc =
      "Modellprojekt-Vorschlag für den Breitscheidplatz: geprüfter Anfahrschutz kombiniert mit Stadtgrün, Sitzinseln und Aufenthaltsqualität — ein Beitrag zur Debatte, wie einer der bekanntesten Plätze Berlins geschützt werden kann, ohne verbaut zu werden.";
    setMeta("description", desc);
    setMeta("og:title", "Breitscheidplatz — Schutz ohne Barrikaden", "property");
    setMeta("og:description", desc, "property");
    setMeta("og:type", "article", "property");
    setMeta("og:url", "https://berlin-cube.de/breitscheidplatz", "property");

    // canonical
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
      created.push(link);
    } else {
      restored.push({ el: link, attr: "href", prev: link.getAttribute("href") });
    }
    link.setAttribute("href", "https://berlin-cube.de/breitscheidplatz");

    return () => {
      document.title = prevTitle;
      restored.forEach(({ el, attr, prev }) => {
        if (prev === null) el.removeAttribute(attr);
        else el.setAttribute(attr, prev);
      });
      created.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
    };
  }, []);

  const { lang } = useLang();
  const morgenpostHref =
    "https://www.morgenpost.de/bezirke/charlottenburg-wilmersdorf/article412587995/terrorschutz-am-breitscheidplatz-muss-dem-platz-gerecht-werden.html";

  return (
    <div
      className="min-h-screen bg-[#1F2328] text-white"
      data-testid="page-breitscheidplatz"
    >
      {/* Slim nav */}
      <header className="absolute top-0 left-0 right-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-14 py-6 flex items-center justify-between">
          <Link
            to="/"
            translate="no"
            className="notranslate inline-flex items-center gap-2 font-display font-semibold tracking-[0.24em] text-sm text-white hover:text-[#F5D77A] transition-colors"
            data-testid="bp-nav-home"
          >
            <BrandWordmark lineClass="bg-white/80" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.32em] uppercase text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
            Zurück
          </Link>
        </div>
      </header>

      {/* 1 · HERO */}
      <section
        data-testid="bp-hero"
        className="relative h-[92vh] min-h-[720px] w-full overflow-hidden"
      >
        <img
          src={HERO_BG}
          alt="Breitscheidplatz · heute"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1F2328] via-[#1F2328]/50 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1F2328]/80 via-transparent to-transparent" />

        <div className="absolute inset-x-0 bottom-0 z-10 pb-14 md:pb-24">
          <div className="max-w-[1400px] mx-auto px-6 md:px-14">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-4xl"
            >
              <div className="flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase text-[#F5D77A]/85 mb-8 font-display">
                <span className="h-px w-8 bg-[#F5D77A]" />
                Modellprojekt-Vorschlag
              </div>
              <h1
                className="font-display font-light tracking-[-0.03em] leading-[0.95] text-white"
                style={{ fontSize: "clamp(2.75rem, 7vw, 6.5rem)" }}
                data-testid="bp-hero-title"
              >
                Breitscheidplatz.
                <br />
                Ein Platz, der mehr verdient.
              </h1>
              <p
                className="mt-10 text-white/75 font-display font-light tracking-[-0.005em] leading-[1.4] max-w-3xl"
                style={{ fontSize: "clamp(1.1rem, 1.6vw, 1.55rem)" }}
                data-testid="bp-hero-sub"
              >
                Ein Beitrag zur Debatte, wie einer der bekanntesten Plätze
                Berlins geschützt werden kann — ohne verbaut zu werden.
              </p>
            </motion.div>
          </div>
        </div>

        <div className="absolute bottom-8 right-8 z-10 text-white/60">
          <ArrowDown className="h-5 w-5" strokeWidth={1.4} />
        </div>
      </section>

      {/* 2 · DIE DEBATTE */}
      <section
        data-testid="bp-debate"
        className="relative bg-[#F7F5F1] text-[#1F2328] py-32 md:py-48"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-14">
          <div className="grid lg:grid-cols-12 gap-10 mb-16 md:mb-24">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-3 text-[11px] tracking-[0.4em] uppercase text-[#B18B5E] mb-8 font-display">
                <span className="h-px w-8 bg-[#B18B5E]" />
                Die Debatte
              </div>
              <h2
                className="font-display font-light tracking-[-0.025em] leading-[1.05]"
                style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.5rem)" }}
              >
                Wie schützt man einen Platz, der Berlin ist?
              </h2>
            </div>
            <div className="lg:col-span-4 flex items-end">
              <p className="text-[#1F2328]/70 leading-relaxed">
                Am Breitscheidplatz werden dauerhafte Schutzanlagen geplant.
                Zeitgleich prägen Baustellen das Erscheinungsbild. Die
                Entscheidung über konkrete Maßnahmen liegt beim Land Berlin.
              </p>
            </div>
          </div>

          {/* Quote card — Foster+Partners style */}
          <figure
            data-testid="bp-quote-card"
            className="relative bg-white border border-[#1F2328]/10 p-10 md:p-16 shadow-[0_20px_60px_-30px_rgba(31,35,40,0.15)] max-w-4xl"
          >
            <Quote
              className="absolute top-8 right-8 h-8 w-8 text-[#B18B5E]/40"
              strokeWidth={1.2}
              aria-hidden
            />
            <blockquote
              className="font-display font-light tracking-[-0.03em] leading-[1.05] text-[#1F2328]"
              style={{ fontSize: "clamp(1.85rem, 4.5vw, 4rem)" }}
            >
              {`„Nicht alles verbarrikadieren\u201c`}
            </blockquote>
            <figcaption className="mt-8 text-[10px] tracking-[0.4em] uppercase text-[#1F2328]/55 font-display">
              — Berliner Morgenpost · 17. Juli 2026
            </figcaption>
          </figure>

          <p
            className="mt-14 max-w-3xl font-display font-light text-[#1F2328]/75 tracking-[-0.005em] leading-[1.6]"
            style={{ fontSize: "clamp(1.05rem, 1.3vw, 1.3rem)" }}
          >
            Die Berliner Morgenpost berichtet über den Termin mit
            Bezirksbürgermeisterin Kirstin Bauch und der AG City an der
            Kantstraße — und über die Debatte, wie der Breitscheidplatz
            geschützt werden kann, ohne verbaut zu werden.
          </p>

          <a
            href={morgenpostHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="bp-article-link"
            className="group mt-12 inline-flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase font-display text-[#B18B5E] hover:text-[#1F2328] transition-colors border-b border-[#B18B5E]/40 hover:border-[#1F2328] pb-1.5"
          >
            Zum Artikel
            <ArrowUpRight
              className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              strokeWidth={1.5}
            />
          </a>
        </div>
      </section>

      {/* 3 · VORHER / NACHHER */}
      <section
        data-testid="bp-transform"
        className="relative bg-[#1F2328] text-white py-32 md:py-48"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-14">
          <div className="mb-16 md:mb-24 max-w-4xl">
            <div className="flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase text-[#F5D77A]/80 mb-8 font-display">
              <span className="h-px w-8 bg-[#F5D77A]" />
              Perspektiven
            </div>
            <h2
              className="font-display font-light tracking-[-0.03em] leading-[0.98]"
              style={{ fontSize: "clamp(2.5rem, 6.5vw, 5.5rem)" }}
            >
              Derselbe Platz. Zwei Möglichkeiten.
            </h2>
            <p
              className="mt-8 text-white/70 leading-relaxed max-w-2xl"
              style={{ fontSize: "clamp(1rem, 1.15vw, 1.15rem)" }}
            >
              Links: der heutige Zustand. Rechts: eine Konzeptvisualisierung
              — kein realisiertes Projekt, sondern ein Gedankenanstoß.
            </p>
          </div>

          <div className="space-y-16 md:space-y-24">
            {PAIRS.map((p, i) => (
              <motion.div
                key={p.key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{
                  duration: 1.1,
                  delay: i * 0.1,
                  ease: [0.22, 1, 0.36, 1],
                }}
                data-testid={`bp-pair-${p.key}`}
              >
                <div className="flex items-baseline justify-between mb-4">
                  <div className="text-[10px] tracking-[0.35em] uppercase text-white/45 font-display">
                    Perspektive · {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="text-[13px] text-white/60">{p.label}</div>
                </div>
                <BeforeAfterSlider
                  before={p.before}
                  after={p.after}
                  afterFocus={p.afterFocus}
                  label={p.label}
                />
              </motion.div>
            ))}
          </div>

          <p className="mt-14 text-[12px] text-white/45 italic max-w-2xl leading-relaxed border-l-2 border-[#F5D77A]/40 pl-5">
            Schieberegler nach links oder rechts ziehen. Alle Nachher-Bilder
            sind Konzeptvisualisierungen — keine realisierten Anlagen.
          </p>
        </div>
      </section>

      {/* 4 · DER VORSCHLAG */}
      <section
        data-testid="bp-proposal"
        className="relative bg-white text-[#1F2328] py-32 md:py-48"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-14">
          <div className="grid lg:grid-cols-12 gap-10 mb-16 md:mb-24">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase text-[#B18B5E] mb-8 font-display">
                <span className="h-px w-8 bg-[#B18B5E]" />
                Der Vorschlag
              </div>
              <h2
                className="font-display font-light tracking-[-0.03em] leading-[1]"
                style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}
              >
                Was Berlin-Cube beitragen könnte.
              </h2>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1F2328]/10 border border-[#1F2328]/10">
            {PROPOSAL_CARDS.map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.div
                  key={c.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{
                    duration: 0.8,
                    delay: i * 0.08,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  data-testid={`bp-proposal-card-${i}`}
                  className="bg-white p-10 md:p-12 flex flex-col gap-6 min-h-[300px]"
                >
                  <div className="flex items-center justify-between">
                    <Icon
                      className="h-6 w-6 text-[#B18B5E]"
                      strokeWidth={1.4}
                      aria-hidden
                    />
                    <span className="text-[10px] tracking-[0.35em] uppercase text-[#1F2328]/35 font-display">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div>
                    <h3
                      className="font-display font-light tracking-[-0.02em] leading-[1.1] text-[#1F2328]"
                      style={{ fontSize: "clamp(1.35rem, 2vw, 1.85rem)" }}
                    >
                      {c.title}
                    </h3>
                    <p className="mt-4 text-[14px] leading-relaxed text-[#1F2328]/65">
                      {c.body}
                    </p>
                    {c.linkLabel && (
                      <a
                        href={c.linkHref}
                        className="mt-6 inline-flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase font-display text-[#B18B5E] hover:text-[#1F2328] transition-colors border-b border-[#B18B5E]/40 hover:border-[#1F2328] pb-1"
                      >
                        {c.linkLabel}
                        <ArrowUpRight
                          className="h-3 w-3"
                          strokeWidth={1.5}
                        />
                      </a>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 5 · EHRLICHE EINORDNUNG */}
      <section
        data-testid="bp-honest"
        className="relative bg-[#F7F5F1] text-[#1F2328] py-28 md:py-40"
      >
        <div className="max-w-[1000px] mx-auto px-6 md:px-14">
          <div className="flex items-center gap-3 text-[11px] tracking-[0.4em] uppercase text-[#B18B5E] mb-10 font-display">
            <span className="h-px w-8 bg-[#B18B5E]" />
            Ehrliche Einordnung
          </div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1 }}
            className="font-display font-light tracking-[-0.015em] leading-[1.4] text-[#1F2328]/85"
            style={{ fontSize: "clamp(1.25rem, 2vw, 1.85rem)" }}
            data-testid="bp-honest-text"
          >
            Der Breitscheidplatz könnte — vorbehaltlich aller politischen,
            fachlichen, rechtlichen, technischen und finanziellen Abstimmungen
            — als möglicher Demonstrationsstandort untersucht werden. Die
            Entscheidung liegt beim Land Berlin. Diese Seite ist ein Angebot
            zum Dialog, keine beschlossene Planung.
          </motion.p>
        </div>
      </section>

      {/* 5b · FAQ */}
      <section
        data-testid="bp-faq"
        className="relative bg-white text-[#1F2328] py-28 md:py-40"
      >
        <div className="max-w-[1000px] mx-auto px-6 md:px-14">
          <div className="flex items-center gap-3 text-[11px] tracking-[0.4em] uppercase text-[#B18B5E] mb-10 font-display">
            <span className="h-px w-8 bg-[#B18B5E]" />
            Häufige Fragen
          </div>
          <h2
            className="font-display font-light tracking-[-0.02em] leading-[1.1]"
            style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.8rem)" }}
            data-testid="bp-faq-title"
          >
            Fragen zum Breitscheidplatz
          </h2>
          <div className="mt-10 divide-y divide-[#1F2328]/10 border-b border-[#1F2328]/10">
            {BP_FAQ.map((f, i) => (
              <details key={i} className="group py-5" data-testid={`bp-faq-${i}`}>
                <summary className="flex items-start justify-between gap-6 cursor-pointer list-none text-[#1F2328]/85 hover:text-[#1F2328] transition-colors font-display font-light text-lg md:text-xl tracking-[-0.01em]">
                  {f.q}
                  <Plus className="mt-1.5 h-4 w-4 shrink-0 text-[#B18B5E] transition-transform group-open:rotate-45" strokeWidth={1.5} />
                </summary>
                <p className="mt-4 text-[#1F2328]/65 leading-[1.7] text-[15px] md:text-base pr-10">{f.a}</p>
              </details>
            ))}
          </div>

          <div className="mt-14">
            <div className="text-[11px] tracking-[0.4em] uppercase text-[#B18B5E] mb-6 font-display">
              Weitere Anwendungen
            </div>
            <div className="flex flex-col gap-3">
              <Link
                to="/zufahrtsschutz-weihnachtsmarkt"
                data-testid="bp-related-weihnachtsmarkt"
                className="group inline-flex items-center gap-3 text-[#1F2328]/75 hover:text-[#B18B5E] transition-colors w-fit font-display font-light text-lg md:text-xl tracking-[-0.01em]"
              >
                Zufahrtsschutz für Weihnachtsmärkte
                <ArrowUpRight className="h-4 w-4 opacity-50 transition-all group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
              </Link>
              <Link
                to="/begruenter-zufahrtsschutz-statt-poller"
                data-testid="bp-related-poller"
                className="group inline-flex items-center gap-3 text-[#1F2328]/75 hover:text-[#B18B5E] transition-colors w-fit font-display font-light text-lg md:text-xl tracking-[-0.01em]"
              >
                Begrünter Zufahrtsschutz statt Poller
                <ArrowUpRight className="h-4 w-4 opacity-50 transition-all group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 6 · CTA */}
      <section
        data-testid="bp-cta"
        className="relative bg-[#0F1114] text-white py-32 md:py-48 overflow-hidden"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-14">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase text-[#F5D77A]/80 mb-10 font-display">
              <span className="h-px w-8 bg-[#F5D77A]" />
              Dialog
            </div>
            <h2
              className="font-display font-light tracking-[-0.03em] leading-[0.98]"
              style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}
            >
              Wir stellen das Konzept gern vor — in Berlin oder in Ihrem Haus.
            </h2>
            <p className="mt-8 text-white/70 leading-relaxed max-w-2xl">
              Termine, Unterlagen und ein direkter Kontakt zur
              Geschäftsführung — auf Anfrage.
            </p>
            <div className="mt-12 flex flex-wrap items-center gap-6">
              <Link
                to="/#contact"
                data-testid="bp-cta-contact"
                className="group inline-flex items-center gap-3 bg-[#F5D77A] text-[#1F2328] px-8 py-4 text-[11px] tracking-[0.35em] uppercase font-display hover:bg-white transition-colors"
              >
                Gespräch anfragen
                <ArrowUpRight
                  className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  strokeWidth={1.5}
                />
              </Link>
              <Link
                to="/"
                className="group inline-flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase font-display text-white/70 hover:text-white transition-colors border-b border-white/20 hover:border-white pb-1.5"
              >
                Zur Hauptseite
                <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
